/**
 * **영토전의 차례**(Todo9 6단계). 세계는 실제 시간이 아니라 차례로 돈다 — 출정 한 번(철수·전멸, 토벌 빼고)이나 영토 전투
 * 한 판이 끝날 때 한 차례(`realmTick`). 한 차례에 일어나는 일, 이 순서로:
 *
 * 1. 다친 병사 가운데 나은 사람이 병영으로 돌아온다.
 * 2. 수비대가 군자금을 낸다(`realmIncome`).
 * 3. **각 나라의 이름난 장수가 친다**(우리 나라 빼고, 고구려·백제·신라 순). **땅이 가장 넓은 나라**(제 나라 빼고, 우리 나라
 *    포함)를 적으로 골라 그 나라의 맞닿은 땅 하나를 친다 — 먼저 제 옛 땅(`warTarget`). 그 나라와 맞닿은 땅이 없으면 다음으로
 *    넓은 나라. 넓이가 같으면 시드 주사위. 그래서 나라끼리도 싸운다 — 앞서가는 나라가 표적이 되어 한쪽이 굴러 커지는 것도
 *    막는다(사용자가 고른 규칙: "땅이 가장 넓은 나라를 치는 거야"). 자동으로 판정한다(`resolveAttack`). 전장을 돌리지 않는다: 한 차례에 몇 판이 나는데 그때마다
 *    전장을 돌리면 모바일에서 출정이 끝날 때마다 멈춘다. 대신 양쪽 전투력을 견준 주사위다.
 *
 * **고무줄**: 땅을 많이 잃은 나라일수록 세게(공격 깊이 +2/잃은 땅) 자주(30% + 15%/잃은 땅, 90%까지) 온다 — 한쪽이 굴러
 * 커지는 것을 막고, 많이 빼앗을수록 지키기가 어렵다. **유예**: 막 주인이 바뀐 땅은 두 차례 되찾으러 오지 않는다(핑퐁 막기).
 *
 * **수비대의 생사**(사용자 결정): 막아 내면 병사마다 일부가 다치고(두 차례 쉰다), 나머지는 경험치를 받고 군자금이
 * 들어온다. 땅을 빼앗기면 그 판에서만 일부 전사, 나머지는 다쳐서 돌아온다. 다치는·죽는 비율은 **양쪽 힘의 비**를 따른다
 * (`heldWoundPct`·`lostDeathPct`) — 넉넉히 막으면 덜 다치고, 크게 밀려 빼앗기면 더 죽는다. **동료는 죽지 않는다** — 쉬러
 * 돌아온다.
 *
 * 주사위는 시드 난수(`Rng`)이고 시드는 차례와 나라에서 나온다 — 같은 계정 상태면 같은 결말이다(시험·나중의 서버 검증).
 */
import { Rng } from '../sim/rng.ts';
import { Culture, boostSpec, companionsOf, depthRoster, foeSubsPower, specForTroop } from '../sim/troops.ts';
import type { CompanionDef } from '../sim/troops.ts';
import { dressCompanion } from '../sim/scenario.ts';
import { COMPANION_REST, battleXp, companionLevelOf, isElite, soldierLevel } from './army.ts';
import type { Army, Soldier } from './army.ts';
import { realmIncome } from './garrison.ts';
import { specialtyPhase } from './specialty.ts';
import { seasonStep } from './season.ts';
import { LOG_KEEP, TERRITORIES, garrisonDepth, neighborsOf, territoriesOf } from './realm.ts';
import type { RealmEvent, RealmState, TerritoryDef } from './realm.ts';
import { loadoutOf } from './stash.ts';

/**
 * 막 주인이 바뀐 땅을 되찾으러 오지 않는, 바뀐 뒤의 차례 수. `heldSince`는 **바뀐 일이 정산된 차례**다 — 우리가 빼앗으면
 * 그 판이 돌리는 차례(지금 + 1), 장수가 빼앗으면 그 차례. 그 차례와 뒤의 `GRACE_TURNS` 차례 동안 막는다. 처음에는 우리가
 * 빼앗을 때 아직 오르지 않은 차례를 적어, 곧 오르는 차례가 이미 한 차례 지난 것으로 셌다 — "두 차례"가 실제로는 한
 * 차례였고, 지도는 "1차례 동안"이라 적은 채 다음 차례에 도로 빼앗겼다(리뷰·QA가 짚었다).
 */
export const GRACE_TURNS = 2;
/** 다친 병사가 쉬는 차례 수. */
export const WOUND_TURNS = 2;
/**
 * 공격이 차지하는 힘의 몫(0–100, 공격 ÷ (공격 + 막는 힘)). 판정의 이길 몫과 같은 수다 — 50이면 맞먹는다.
 */
export function attackShare(att: number, def: number): number {
  return att + def > 0 ? Math.floor((att * 100) / (att + def)) : 50;
}

/**
 * 막아 낸 판에서 수비 병사가 다칠 확률(%): 공격의 몫 × 0.4, 5–40%. 맞먹으면 20%. 처음에는 늘 20%였다 — 열 배 센 수비대로
 * 막아도, 겨우 막아도 같은 비율로 다쳤다. 사용자가 청했다: "양쪽 병력의 상대적 강함에 영향을 받도록."
 */
export function heldWoundPct(att: number, def: number): number {
  return Math.max(5, Math.min(40, Math.floor((attackShare(att, def) * 40) / 100)));
}

/**
 * 땅을 빼앗긴 판에서 수비 병사가 전사할 확률(%): 공격의 몫 × 0.6, 10–60%. 맞먹으면 30%(처음의 고정값). 크게 밀려 빼앗기면
 * 많이 죽고, 맞서다 진 판은 덜 죽는다. 나머지는 다쳐서 돌아온다.
 */
export function lostDeathPct(att: number, def: number): number {
  return Math.max(10, Math.min(60, Math.floor((attackShare(att, def) * 60) / 100)));
}
/** 잃은 땅 하나마다 공격 깊이에 더하는 몫(고무줄). */
export const RUBBER_DEPTH = 2;
/** 공격 확률: 기본 + 잃은 땅 하나마다, 상한. */
const ATTACK_BASE_PCT = 30;
const ATTACK_PER_LOST_PCT = 15;
const ATTACK_MAX_PCT = 90;

const CULTURES = [Culture.Goguryeo, Culture.Baekje, Culture.Silla] as const;

function rosterPower(roster: readonly { troop: string; count: number; level: number; boost?: number }[]): number {
  return roster.reduce((n, e) => n + boostSpec(specForTroop(e.troop, e.level), e.boost).power * e.count, 0);
}

/** 그 나라가 잃은 제 옛 땅의 수. 고무줄이 이것으로 센다. */
export function lostCount(realm: RealmState, culture: Culture): number {
  return TERRITORIES.filter((t) => t.culture === culture && realm.owner[t.id] !== culture).length;
}

/** 그 나라 장수가 그 땅을 칠 때의 공격 깊이(수비 깊이 + 고무줄). */
export function attackDepth(realm: RealmState, by: Culture, t: TerritoryDef): number {
  return garrisonDepth(t.id) + RUBBER_DEPTH * lostCount(realm, by);
}

/** 공격 전투력: 그 나라 병종, 공격 깊이, 그 땅의 지형. */
export function attackPower(realm: RealmState, by: Culture, t: TerritoryDef): number {
  // 부장의 격을 더한다(`foeSubsPower`) — 부장은 명부의 병사 값에서 치른 몫이다.
  const d = attackDepth(realm, by, t);
  return rosterPower(depthRoster(by, d, [t.land])) + foeSubsPower(by, d, [t.land]);
}

/**
 * 막는 전투력: 그 땅의 시스템 수비대(주인의 병종·수비 깊이·지형) + 우리 수비대(병사의 몸 격 + 대장). 우리 땅이 아니면
 * 시스템 수비대만.
 */
export function defensePower(army: Army, realm: RealmState, t: TerritoryDef): number {
  let p = rosterPower(depthRoster(realm.owner[t.id], garrisonDepth(t.id), [t.land])) + foeSubsPower(realm.owner[t.id], garrisonDepth(t.id), [t.land]);
  const g = realm.garrisons?.[t.id];
  if (g) {
    // 병사마다의 성장(uid)까지 — 출정 편성의 전투력(`selectionPower`)과 같은 몸이다. 빼면 병종·레벨 평균이 됐다(리뷰).
    for (const s of g.soldiers) p += specForTroop(s.troop, soldierLevel(s), true, s.uid, isElite(s)).power;
    const def = g.captain ? companionsOf(army.culture).find((d) => d.id === g.captain) : undefined;
    if (def) p += dressCompanion(def, companionLevelOf(army, def.id), loadoutOf(army, def.id), army.culture).power;
  }
  return p;
}

/** 지금 차례(장수의 차례 안이면 그 차례)에 유예 중인가 — 바뀐 차례부터 `GRACE_TURNS` 차례 뒤까지. */
export function inGrace(realm: RealmState, id: string): boolean {
  const at = realm.heldSince?.[id];
  return at !== undefined && realm.turn - at <= GRACE_TURNS;
}

/** 앞으로 남은 유예 차례 수(지도가 적는다). 0이면 다음 차례에 칠 수 있다. */
export function graceLeft(realm: RealmState, id: string): number {
  const at = realm.heldSince?.[id];
  return at === undefined ? 0 : Math.max(0, at + GRACE_TURNS - realm.turn);
}

/**
 * 그 나라 장수가 칠 수 있는 `victim` 나라의 땅: 수도 아님, 그 나라 땅과 맞닿음, 유예 밖. 제 옛 땅이 먼저다(`TERRITORIES` 순서
 * 안에서). 이름은 처음 쓰임(우리 땅)에서 왔다 — 상대는 어느 나라든 된다.
 */
export function npcTargets(realm: RealmState, by: Culture, victim: Culture): TerritoryDef[] {
  const open = TERRITORIES.filter(
    (t) =>
      !t.capital &&
      realm.owner[t.id] === victim &&
      !inGrace(realm, t.id) &&
      neighborsOf(t.id).some((n) => realm.owner[n] === by),
  );
  const own = open.filter((t) => t.culture === by);
  return own.length > 0 ? own : open;
}

/**
 * 그 나라 장수가 고르는 적과 칠 땅들: 제 나라 빼고 땅이 가장 넓은 나라, 그 나라와 맞닿은 칠 땅이 없으면 다음으로 넓은 나라.
 * 넓이가 같으면 `tie`(0 이상 정수, 시드 주사위)로 고른다 — 정해진 순서로 가르면 늘 같은 나라가 표적이 된다. 칠 곳이 없으면 null.
 */
export function warTarget(realm: RealmState, by: Culture, tie: number): { victim: Culture; targets: TerritoryDef[] } | null {
  const others = CULTURES.filter((c) => c !== by).map((c) => ({ c, n: territoriesOf(realm, c).length }));
  // 넓은 순, 같으면 주사위가 정한 쪽이 앞(두 나라뿐이라 tie의 홀짝으로 가른다).
  others.sort((a, b) => b.n - a.n || (tie % 2 === 0 ? a.c - b.c : b.c - a.c));
  for (const { c } of others) {
    const targets = npcTargets(realm, by, c);
    if (targets.length > 0) return { victim: c, targets };
  }
  return null;
}

/** 다친 병사 가운데 나은 사람을 병영으로(uid 순). */
function releaseInfirmary(army: Army, realm: RealmState): void {
  if (!realm.infirmary) return;
  const back = realm.infirmary.filter((x) => x.until <= realm.turn);
  if (back.length === 0) return;
  realm.infirmary = realm.infirmary.filter((x) => x.until > realm.turn);
  if (realm.infirmary.length === 0) delete realm.infirmary;
  army.soldiers.push(...back.map((x) => x.soldier));
  army.soldiers.sort((a, b) => a.uid - b.uid);
}

/** 한 판의 자동 판정. 공격이 이기면 땅이 그 나라로 간다. 소식을 적는다. */
export function resolveAttack(army: Army, realm: RealmState, by: Culture, t: TerritoryDef, rng: Rng): void {
  const att = attackPower(realm, by, t);
  const def = defensePower(army, realm, t);
  const generals: CompanionDef[] = companionsOf(by);
  const general = generals[rng.int(generals.length)].id;
  // 이길 확률은 전투력의 몫(공격 ÷ (공격 + 막기)). 주사위는 판마다 정확히 한 번 굴린다.
  const lost = rng.int(Math.max(1, att + def)) < att;
  const victim = realm.owner[t.id];
  // 우리 수비대는 우리 땅에만 있다 — 나라끼리의 싸움에서는 없다.
  const g = victim === army.culture ? realm.garrisons?.[t.id] : undefined;
  let dead = 0;
  let hurt = 0;
  let bounty = 0;
  const wound = (s: Soldier): void => {
    (realm.infirmary ??= []).push({ soldier: s, until: realm.turn + WOUND_TURNS });
    hurt++;
  };
  if (lost) {
    realm.owner[t.id] = by;
    (realm.heldSince ??= {})[t.id] = realm.turn;
    if (g) {
      // 병사마다 한 번씩 굴린다(수비대 순서). 그 판에서만 일부 전사, 나머지는 다쳐서 돌아온다.
      for (const s of g.soldiers) {
        if (rng.chance(lostDeathPct(att, def))) dead++;
        else wound(s);
      }
      army.fallen += dead;
      if (g.captain) {
        const st = army.companions.find((c) => c.id === g.captain);
        if (st) {
          delete st.garrison;
          st.readyAt = Math.max(st.readyAt, army.sortieCount + COMPANION_REST);
        }
      }
      delete realm.garrisons![t.id];
      if (Object.keys(realm.garrisons!).length === 0) delete realm.garrisons;
    }
  } else if (g) {
    // 막아 냈다: 다친 병사는 쉬러 가고, 남은 병사는 그 깊이의 판을 이긴 만큼 경험치를 받는다. 군자금은 친 쪽 전투력에 비례.
    const stay: typeof g.soldiers = [];
    for (const s of g.soldiers) {
      if (rng.chance(heldWoundPct(att, def))) wound(s);
      else {
        s.xp += battleXp(attackDepth(realm, by, t));
        stay.push(s);
      }
    }
    g.soldiers = stay;
    bounty = Math.floor(att / 100);
    army.gold += bounty;
    if (g.soldiers.length === 0 && g.captain === null) delete realm.garrisons![t.id];
  }
  if (victim !== army.culture) {
    // 나라끼리의 싸움: 우리 것이 아니라 소식만 적는다(빼앗겼으면 'war', 막았으면 'repel').
    pushLog(realm, { turn: realm.turn, kind: lost ? 'war' : 'repel', territory: t.id, by, victim, general, dead: 0, hurt: 0, bounty: 0 });
    return;
  }
  pushLog(realm, { turn: realm.turn, kind: lost ? 'lost' : 'held', territory: t.id, by, general, dead, hurt, bounty });
}

/** 소식을 적는다(`LOG_KEEP`개까지). */
export function pushLog(realm: RealmState, e: RealmEvent): void {
  const log = (realm.log ??= []);
  log.push(e);
  if (log.length > LOG_KEEP) log.splice(0, log.length - LOG_KEEP);
}

/** 각 나라 장수의 차례(우리 나라 빼고, 정해진 순서). 한 나라는 한 차례에 한 판까지. */
function npcPhase(army: Army, realm: RealmState): void {
  for (const by of CULTURES) {
    if (by === army.culture || territoriesOf(realm, by).length === 0) continue;
    // 나라마다 따로 굴린다(시드: 차례·우리 나라·친 나라). 한 줄로 이어 굴렸더니 앞 나라가 판정·병사 수만큼 주사위를 더
    // 써서, 앞 나라의 사정이 뒤 나라의 결말을 바꿨다(리뷰가 짚었다). 같은 계정 상태면 같은 결말인 것은 그대로다.
    const rng = new Rng((Math.imul(realm.turn + 1, 0x9e3779b1) ^ Math.imul(army.culture + 1, 0x85ebca6b) ^ Math.imul(by + 1, 0xc2b2ae35)) >>> 0);
    // 주사위 수는 늘 같게(칠 확률·넓이 동률·땅 고르기 세 번) — 칠 곳이 없어도.
    const roll = rng.int(100);
    const tie = rng.int(2);
    const war = warTarget(realm, by, tie);
    const targets = war?.targets ?? [];
    const pick = rng.int(Math.max(1, targets.length));
    if (targets.length === 0) continue;
    const pct = Math.min(ATTACK_MAX_PCT, ATTACK_BASE_PCT + ATTACK_PER_LOST_PCT * lostCount(realm, by));
    if (roll >= pct) continue;
    resolveAttack(army, realm, by, targets[pick], rng);
  }
}

/**
 * 세계가 한 차례 돈다. 출정 한 번(철수·전멸, 토벌 빼고)이나 영토 전투 한 판이 끝날 때 부른다. 영토전을 한 번도 열지 않은
 * 계정이면(세계가 없으면) 아무 일도 없다. 들어온 수입을 돌려준다.
 */
export function realmTick(army: Army): number {
  const realm = army.realm;
  if (!realm) return 0;
  realm.turn++;
  releaseInfirmary(army, realm);
  const { total } = realmIncome(realm);
  army.gold += total;
  realm.lastIncome = { turn: realm.turn, gold: total };
  // 특산(지킨 땅이 쌓는 점수, 넘으면 물건) — 장수의 공격보다 먼저: 이 차례 내내 지킨 몫이다.
  for (const g of specialtyPhase(army, realm)) pushLog(realm, { turn: realm.turn, kind: 'gift', territory: g.territory, item: g.item, by: army.culture, general: '', dead: 0, hurt: 0, bounty: 0 });
  npcPhase(army, realm);
  // 시즌(누적 보유 점수): 장수의 차례 뒤의 땅으로 센다 — 그 차례를 버티고 쥔 땅이다. 60차례가 차면 매기고 지도를 되돌린다.
  seasonStep(army, realm);
  return total;
}
